<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import {
    Activity,
    BookMarked,
    BookOpen,
    FileText,
    LayoutDashboard,
    Link2,
    Network,
    SearchCode,
    Settings,
    Table2,
    Upload,
} from 'lucide-vue-next';
import { computed } from 'vue';
import AppLogo from '@/components/AppLogo.vue';
import NavMain from '@/components/NavMain.vue';
import NavUser from '@/components/NavUser.vue';
import ProjectSwitcher from '@/components/ProjectSwitcher.vue';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
} from '@/components/ui/sidebar';
import { useEditorOutline } from '@/composables/useEditorOutline';
import { dashboard } from '@/routes';
import * as projectRoutes from '@/routes/projects';
import * as projectArticlesRoutes from '@/routes/projects/articles';
import * as projectAssociationsRoutes from '@/routes/projects/associations';
import * as projectDataRoutes from '@/routes/projects/data';
import * as projectReferencesRoutes from '@/routes/projects/references';
import type { NavItem, SidebarProject } from '@/types';

defineProps<{
    notifications?: any;
}>();

const page = usePage();
const sidebarProjects = computed<SidebarProject[]>(
    () => (page.props.sidebarProjects as SidebarProject[]) ?? [],
);

const currentProject = computed(
    () => (page.props.project as { id: string; name: string } | null) ?? null,
);

const projectMainNavItems = computed<NavItem[]>(() => {
    const p = currentProject.value;

    if (!p) {
        return [];
    }

    return [
        {
            title: "Vue d'ensemble",
            href: projectRoutes.show(p.id).url,
            icon: LayoutDashboard,
        },
        {
            title: 'Rédaction',
            href: projectRoutes.editor(p.id).url,
            icon: FileText,
        },
        {
            title: 'Articles',
            href: projectArticlesRoutes.index(p.id).url,
            icon: BookOpen,
        },
        {
            title: 'Données',
            href: projectDataRoutes.index(p.id).url,
            icon: Table2,
        },
        {
            title: 'Bibliographie',
            href: projectReferencesRoutes.index(p.id).url,
            icon: BookMarked,
        },
    ];
});

const projectPreparationNavItems = computed<NavItem[]>(() => {
    const p = currentProject.value;

    if (!p) {
        return [];
    }

    return [
        {
            title: 'Configuration',
            href: projectRoutes.settings(p.id).url,
            icon: Settings,
        },
        {
            title: 'Équations de recherche',
            href: projectRoutes.searchEquations(p.id).url,
            icon: SearchCode,
        },
        {
            title: 'Import',
            href: projectRoutes.importMethod(p.id).url,
            icon: Upload,
        },
        {
            title: 'Associations PDF',
            href: projectAssociationsRoutes.index(p.id).url,
            icon: Link2,
        },
    ];
});

const projectSuiviNavItems = computed<NavItem[]>(() => {
    const p = currentProject.value;

    if (!p) {
        return [];
    }

    return [
        {
            title: 'Diagramme PRISMA',
            href: projectRoutes.prismaStats(p.id).url,
            icon: Network,
        },
        {
            title: 'Activité',
            href: projectRoutes.activity(p.id).url,
            icon: Activity,
        },
    ];
});

const { headings, onJump } = useEditorOutline();
</script>

<template>
    <Sidebar collapsible="icon" variant="inset">
        <SidebarHeader>
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton size="lg" as-child>
                        <Link :href="dashboard()">
                            <AppLogo />
                        </Link>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>
            <ProjectSwitcher
                :projects="sidebarProjects"
                :current-project="currentProject"
            />
        </SidebarHeader>

        <SidebarContent>
            <template v-if="currentProject">
                <NavMain :items="projectMainNavItems" />
                <SidebarSeparator />
                <NavMain
                    :items="projectPreparationNavItems"
                    label="Préparation"
                />
                <SidebarSeparator />
                <NavMain :items="projectSuiviNavItems" label="Suivi" />
            </template>
            <!-- Document outline — visible only when on the editor page -->
            <template v-if="headings.length > 0">
                <SidebarSeparator />
                <div
                    class="px-3 pt-3 pb-1 text-[10px] font-semibold tracking-wider text-sidebar-foreground/50 uppercase group-data-[collapsible=icon]:hidden"
                >
                    Plan du document
                </div>
                <ScrollArea class="flex-1 group-data-[collapsible=icon]:hidden">
                    <ul class="space-y-0.5 px-2 pb-3">
                        <li v-for="item in headings" :key="item.pos">
                            <button
                                type="button"
                                :class="[
                                    'w-full rounded px-2 py-1 text-left text-xs transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                    item.level === 2
                                        ? 'font-medium text-sidebar-foreground'
                                        : 'pl-5 text-sidebar-foreground/60',
                                ]"
                                @click="onJump?.(item.pos)"
                            >
                                {{ item.text }}
                            </button>
                        </li>
                    </ul>
                </ScrollArea>
            </template>
        </SidebarContent>

        <SidebarFooter>
            <NavUser :notifications="notifications" />
        </SidebarFooter>
    </Sidebar>
</template>
